import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  ColorOptionLabel,
  MiBox,
  MiButton,
  MiDetailPageHeader,
  MiIcon,
  MiIconButton,
  MiTab,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  CONFIGURABLE_COLORS,
  ICONS_WEB,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import {
  mdiChevronLeft,
  mdiContentSaveCheckOutline,
  mdiPencilOutline,
  mdiPlus,
  mdiWindowClose,
} from "@mdi/js";
import * as mdi from "@mdi/js";
import { Footer } from "@mibuildweb/components";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  useTheme,
} from "@mui/material";
import { REASON_TYPES, ROUTE_NAMES, TAB_LIST } from "./constants";
import { useManageOrderReasonsViewModel } from "./useManageOrderReasonsViewModel";

const ManageReasonsList = ({
  fieldArray,
  fieldName,
  name,
  validateForm,
  errors,
}) => {
  const { fields, append, remove, update } = fieldArray;
  const theme = useTheme();
  const { classes } = useStyles();

  const [editingIndex, setEditingIndex] = useState(-1);
  const [inputReason, setInputReason] = useState({
    value: "",
    color: "",
    requireReapproval: false,
  });
  const [editReason, setEditReason] = useState({
    value: "",
    color: "",
    requireReapproval: false,
  });

  const handleAddItem = () => {
    if (inputReason.value.trim()) {
      append({
        value: inputReason.value.trim(),
        color: inputReason.color,
        requireReapproval: inputReason.requireReapproval,
      });
      validateForm();
      setInputReason({ value: "", color: "", requireReapproval: false });
    }
  };

  const handleDeleteItem = (index) => {
    remove(index);
    validateForm();
  };

  const startEditing = (index, field) => {
    setEditingIndex(index);
    setEditReason({
      id: field.id,
      value: field.value,
      color: field.color,
      requireReapproval: field.requireReapproval || false,
    });
  };

  const saveEdit = (index) => {
    if (editReason.value.trim()) {
      update(index, {
        id: editReason.id,
        value: editReason.value.trim(),
        color: editReason.color,
        requireReapproval: editReason.requireReapproval,
      });
      validateForm();
      setEditingIndex(-1);
      setEditReason({ value: "", color: "", requireReapproval: false });
    }
  };

  return (
    <Box className={classes.reasonListContainer}>
      <Box className={classes.reasonInputContainer}>
        <TextField
          fullWidth
          size="small"
          value={inputReason.value}
          onChange={(e) =>
            setInputReason({ ...inputReason, value: e.target.value })
          }
          placeholder={`Add new ${name}`}
          variant="outlined"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              handleAddItem();
            }
          }}
        />
        <FormControl size="small" className={classes.colorSelect}>
          <InputLabel>Color</InputLabel>
          <Select
            value={inputReason.color}
            onChange={(e) =>
              setInputReason({ ...inputReason, color: e.target.value })
            }
            label="Color"
          >
            <MenuItem value="">None</MenuItem>
            {CONFIGURABLE_COLORS.map(({ color, label }) => (
              <MenuItem key={color} value={color}>
                <ColorOptionLabel color={color} label={label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={inputReason.requireReapproval}
              onChange={(e) =>
                setInputReason({
                  ...inputReason,
                  requireReapproval: e.target.checked,
                })
              }
              size="small"
            />
          }
          label="Require Reapproval"
        />
        <MiButton
          variant={BUTTON_VARIANTS.OUTLINED}
          onClick={handleAddItem}
          title="ADD"
          startIcon={{
            path: mdiPlus,
            iconSize: 0.8,
          }}
        />
      </Box>
      <List className={classes.listItemContainer}>
        {fields.map((field, index) => (
          <ListItem
            key={field.id}
            className={`${classes.listItem} ${
              errors?.[fieldName]?.[index]?.value ? classes.listItemError : ""
            }`}
          >
            {editingIndex === index ? (
              <Box className={classes.editContainer}>
                <TextField
                  fullWidth
                  size="small"
                  value={editReason.value}
                  onChange={(e) =>
                    setEditReason({ ...editReason, value: e.target.value })
                  }
                  variant="outlined"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      saveEdit(index);
                    }
                  }}
                />
                <FormControl size="small" className={classes.colorSelect}>
                  <InputLabel>Color</InputLabel>
                  <Select
                    value={editReason.color}
                    onChange={(e) =>
                      setEditReason({ ...editReason, color: e.target.value })
                    }
                    label="Color"
                  >
                    <MenuItem value="">None</MenuItem>
                    {CONFIGURABLE_COLORS.map(({ color, label }) => (
                      <MenuItem key={color} value={color}>
                        <ColorOptionLabel color={color} label={label} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editReason.requireReapproval}
                      onChange={(e) =>
                        setEditReason({
                          ...editReason,
                          requireReapproval: e.target.checked,
                        })
                      }
                      size="small"
                    />
                  }
                  label="Require Reapproval"
                />
              </Box>
            ) : (
              <Box className={classes.displayContainer}>
                <Box className={classes.reasonInfo}>
                  <ListItemText
                    primary={field.value}
                    secondary={
                      field.color
                        ? `Color: ${field.color}${
                            field.requireReapproval
                              ? " • Require Reapproval: Yes"
                              : ""
                          }`
                        : errors?.[fieldName]?.[index]?.value?.message
                    }
                    secondaryTypographyProps={{
                      color: errors?.[fieldName]?.[index]?.value
                        ? "error.main"
                        : "text.secondary",
                    }}
                    className={classes.listItemText}
                  />
                </Box>
                {field.color && (
                  <Box
                    className={classes.colorPreview}
                    style={{ backgroundColor: field.color }}
                  />
                )}
              </Box>
            )}
            <ListItemSecondaryAction>
              {editingIndex === index ? (
                <>
                  <MiIconButton
                    data-testid="save-edit-button"
                    onClick={() => saveEdit(index)}
                    className={classes.iconHover}
                  >
                    <MiIcon
                      color={theme.palette.primary.main}
                      path={mdiContentSaveCheckOutline}
                    />
                  </MiIconButton>
                  <MiIconButton
                    data-testid="close-edit-button"
                    onClick={() => setEditingIndex(-1)}
                    className={classes.iconHover}
                  >
                    <MiIcon
                      color={theme.palette.red.main}
                      path={mdiWindowClose}
                    />
                  </MiIconButton>
                </>
              ) : (
                <>
                  <MiIconButton
                    data-testid="edit-button"
                    onClick={() => startEditing(index, field)}
                    className={classes.iconHover}
                  >
                    <MiIcon
                      color={theme.palette.black.main}
                      path={mdiPencilOutline}
                    />
                  </MiIconButton>
                  <MiIconButton
                    data-testid="delete-button"
                    onClick={() => handleDeleteItem(index)}
                    className={classes.iconHover}
                  >
                    <MiIcon
                      path={mdi[ICONS_WEB.TRASH_CAN]}
                      color={BUTTON_COLORS.RED}
                    />
                  </MiIconButton>
                </>
              )}
            </ListItemSecondaryAction>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export function ManageOrderReasons() {
  const router = useRouter();
  const { activeTab } = router.params;

  const {
    getHoldReasons,
    getRescheduleReasons,
    isLoading,
    methods,
    onSubmit,
    holdReasonsForm,
    rescheduleReasonsForm,
    handleReset,
  } = useManageOrderReasonsViewModel();

  useEffect(() => {
    getHoldReasons();
    getRescheduleReasons();
  }, []);

  const { classes } = useStyles();

  const handleTabChange = (tab) => {
    handleReset();
    router.navigate(`/settings/order-management/order-reasons/${tab}`);
  };

  const customOnBack = () => {
    router.navigate(`/settings/order-management`);
  };

  return (
    <Box className={classes.contentContainer}>
      <MiDetailPageHeader
        title="Order Reasons"
        titleIcon={mdiChevronLeft}
        customOnBack={customOnBack}
        loading={isLoading}
      />
      <MiTab
        tabs={TAB_LIST}
        currentTab={activeTab}
        onTabChange={handleTabChange}
      />
      <MiBox className={classes.container}>
        {activeTab === ROUTE_NAMES.HOLD_REASONS && (
          <ManageReasonsList
            name="hold reason"
            fieldName={REASON_TYPES.HOLD}
            fieldArray={holdReasonsForm}
            validateForm={methods.trigger}
            errors={methods.formState.errors}
          />
        )}
        {activeTab === ROUTE_NAMES.RESCHEDULE_REASONS && (
          <ManageReasonsList
            name="reschedule reason"
            fieldName={REASON_TYPES.RESCHEDULE}
            fieldArray={rescheduleReasonsForm}
            validateForm={methods.trigger}
            errors={methods.formState.errors}
          />
        )}
      </MiBox>
      <Footer
        show={methods.formState.isDirty}
        handleSaveClick={methods.handleSubmit((data) =>
          onSubmit(data, activeTab)
        )}
        handleCancelClick={handleReset}
        disableSave={!methods.formState.isValid || isLoading}
      />
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    height: "inherit",
    overflowY: "auto",
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    height: "inherit",
  },
  inputContainer: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  reasonListContainer: {
    padding: 8,
    width: "70%",
    [theme.breakpoints.down("md")]: {
      width: "100%",
    },
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  reasonInputContainer: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    flex: "0 0 auto",
    alignItems: "center",
    flexWrap: "wrap",
  },
  editContainer: {
    display: "flex",
    gap: theme.spacing(2),
    width: "100%",
    alignItems: "center",
  },
  colorSelect: {
    minWidth: 150,
    flex: "0 0 auto",
  },
  displayContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    width: "100%",
  },
  reasonInfo: {
    flex: 1,
  },
  colorPreview: {
    width: 24,
    height: 24,
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    flex: "0 0 auto",
  },
  iconHover: {
    transition: "transform 0.2s",
    "&:hover": { transform: "scale(1.2)" },
  },
  listItem: {
    backgroundColor: theme.palette.blue.light,
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    paddingRight: theme.spacing(24),
    minHeight: 80,
    transition: "all 0.2s ease",
    "&:hover": {
      boxShadow: theme.shadows[1],
    },
  },
  listItemError: {
    border: `1px solid ${theme.palette.error.main}`,
  },
  listItemText: {
    wordBreak: "break-word",
    "& .MuiListItemText-primary": {
      wordBreak: "break-word",
    },
  },
  listItemContainer: {
    flex: "1 1 auto",
    overflowY: "auto",
    paddingRight: theme.spacing(2),
  },
}));
